import { log } from '@/log'
import { ThreeDAuthHtmlTransitionError } from '../repository/errors'

export type ActionRequest = NoActionRequest | RedirectWithHtmlActionRequest | DisabledActionRequest
type ActionName = ActionRequest['name']

interface BaseActionRequest {
  name: ActionName,
}

export interface NoActionRequest extends BaseActionRequest {
  name: 'no-action',
}

export interface RedirectWithHtmlActionRequest extends BaseActionRequest {
  name: 'redirect-with-html',
  html: string,
}

export interface DisabledActionRequest extends BaseActionRequest {
  name: 'disabled-action',
  reason: string,
}

interface ActionResult<T> {
  name: ActionName,
  result: T,
}

export class ActionRequestList {
  constructor(public readonly list: ActionRequest[]) {}

  map<T>(fn: (request: ActionRequest) => T): T[] {
    return this.list.map(fn)
  }
}

interface ActionRule {
  apply(list: ActionRequestList): ActionRequestList
}

export class AtMostOneRedirectWithHtmlRule implements ActionRule {
  apply(requests: ActionRequestList): ActionRequestList {
    return new ActionRequestList(
      requests.list.reduce<ActionRequest[]>(
        (acc, request) => {
          if (request.name === 'redirect-with-html' && acc.some(r => r.name === 'redirect-with-html')) {
            log.warn({
              message: 'The number of redirect action requests should be at most one.',
              requests,
            })

            return [...acc, { name: 'disabled-action', reason: 'Redirect suppressed' } as DisabledActionRequest]
          }

          return [...acc, request]
        },
        []
      )
    )
  }
}

export class ActionExecutor {
  /** TODO: 必要が出てきたら複数ルールを取れるようにする */
  constructor(private readonly rule: ActionRule) {}

  execute(requests: ActionRequestList): Array<Promise<ActionResult<any>>> {
    return this.rule.apply(requests).list.map((request) => {
      return new ActionBuilder().build(request).execute()
    })
  }
}

interface Action<T> {
  execute(): Promise<ActionResult<T>>
}

export class NoAction implements Action<void> {
  execute(): Promise<ActionResult<void>> {
    return Promise.resolve({
      name: 'no-action',
      result: undefined,
    })
  }
}

export class RedirectWithHtmlAction implements Action<void> {
  constructor(private readonly html: string) {}

  execute(): Promise<ActionResult<void>> {
    try {
      navigateHtml(this.html)

      return Promise.resolve({
        name: 'redirect-with-html',
        result: undefined,
      })
    } catch (e) {
      log.error(e)

      throw new ThreeDAuthHtmlTransitionError()
    }
  }
}

function navigateHtml(html: string): void {
  const blob = new Blob([html], { type: 'text/html' })
  const blobUrl = URL.createObjectURL(blob)

  window.location.href = blobUrl
}

class ActionBuilder {
  build(request: ActionRequest): Action<void> {
    switch (request.name) {
      case 'no-action':
        return new NoAction()
      case 'redirect-with-html':
        return new RedirectWithHtmlAction(request.html)
      default:
        throw new Error(`Unknown action`)
    }
  }
}
